import type { MessageKey } from '../../i18n/messages';
import { resolveCallEventTextForDisplay } from '../../i18n/resolveCallEventText';
import type { ResolvedChatPreview } from './chatListView';

/**
 * Служебные литералы в подписи последнего сообщения — `[image]`, `[note]`, `[audio]` и прочие.
 *
 * Их формирует не клиент, а бэкенд (`ChatPreviewCacheService.previewFromCiphertext`) и локальный
 * `previewTextFromCiphertext` для нерасшифрованных случаев: у сообщения без подписи текста нет
 * вовсе, и в кэш превью кладётся такой вот маркер типа. Показывать его человеку сырым нельзя —
 * «[image]» в списке чатов выглядит куском отладки, — поэтому здесь он переводится в иконку.
 *
 * Набор намеренно шире клиентского: `[checklist]` умеет отдавать только сервер (пустой список
 * дел), и без него иконка появлялась бы не во всех случаях.
 *
 * Осторожно: подпись, которую человек написал руками, тоже может оказаться ровно «[image]» —
 * тогда вместо неё покажется иконка. Так уже работали строки списка чатов; лечится это только
 * отдельным полем типа в API, а не угадыванием по тексту.
 */
export type MediaPreviewKind =
  | 'audio'
  | 'video'
  | 'image'
  | 'note'
  | 'contact'
  | 'article'
  | 'checklist'
  | 'file'
  | 'message';

const MEDIA_PREVIEW_TYPES = {
  '[audio]': 'audio',
  '[video]': 'video',
  '[image]': 'image',
  '[note]': 'note',
  '[contact]': 'contact',
  '[article]': 'article',
  '[checklist]': 'checklist',
  '[file]': 'file',
  '[message]': 'message',
} satisfies Record<string, MediaPreviewKind>;

/**
 * Эмодзи-глиф типа вложения. Один на оба места показа, которые уже переведены на этот хелпер, —
 * строку списка чатов и карточку «Избранного».
 *
 * Есть и ТРЕТЬЕ место, сознательно оставленное на следующий шаг: шапка цитаты ответа/пересылки
 * в ChatScreen (`payloadPreviewText` → `replyPreviewById`). Там те же литералы («[image]»,
 * «[message]») человек всё ещё видит сырыми; перевод цитаты на глифы затрагивает разбор payload
 * и в эту правку не входил.
 */
export const MEDIA_PREVIEW_GLYPH: Record<MediaPreviewKind, string> = {
  audio: '🎵',
  video: '🎬',
  image: '🖼️',
  note: '📝',
  contact: '👤',
  article: '📰',
  checklist: '☑️',
  file: '📎',
  message: '💬',
};

/**
 * Тип вложения по подписи, если вся подпись — служебный литерал.
 *
 * Функция, а не индексный доступ к словарю: у `Record<string, MediaPreviewKind>` TypeScript
 * считает результат всегда определённым, и проверка `if (kind)` держалась только на рантайме.
 */
export function mediaPreviewKind(text: string): MediaPreviewKind | null {
  const key = text.trim();
  return Object.prototype.hasOwnProperty.call(MEDIA_PREVIEW_TYPES, key)
    ? MEDIA_PREVIEW_TYPES[key as keyof typeof MEDIA_PREVIEW_TYPES]
    : null;
}

/**
 * Подпись, готовая к показу: системные строки о звонках («📞 Пропущенный звонок») хранятся в
 * тексте сообщения на языке отправителя, поэтому их перекладывают на язык читателя. Общий
 * хелпер, чтобы строка списка и карточка «Избранного» не расходились в этом поведении.
 */
export function previewForDisplay(
  preview: ResolvedChatPreview,
  t: (key: MessageKey) => string,
): ResolvedChatPreview {
  return { text: resolveCallEventTextForDisplay(preview.text, t), marker: preview.marker };
}
